'use client';

import { useTranslation } from 'react-i18next';

import {
  ChildAvailabilityItem,
  ChildDetails,
} from '@sola/schema/dotcom/children/child.js';
import { countryToIso } from '@sola/schema/dotcom/shared/geo.js';

import { useChildSponsorship } from '../../../hooks/useChildSponsorship';
import { ChildBioDetailsHeading } from '../../../molecules/child-bio-details/child-bio-details-heading';
import { ChildBioInfo } from '../../../molecules/child-bio-details/child-bio-info';
import { SubmitButton } from '../../../molecules/child-bio-details/child-bio-sponsorship-submit-button';
import { ChildBioSummary } from '../../../molecules/child-bio-details/child-bio-summary';
import { ChildBioImage } from '../../../molecules/child-bio-image/child-bio-image';
import { key } from '../../../organisms-cms/child-bio/utils/bioKeys';
import { getNote } from '../../../organisms-cms/child-bio/utils/childNote';

type HelpMeChooseResult = {
  childDetails: ChildDetails;
  childAvailability: ChildAvailabilityItem;
};

export const HelpMeChooseResult: React.FC<HelpMeChooseResult> = ({
  childDetails,
  childAvailability,
}) => {
  const { t } = useTranslation(['bio', 'geo', 'help-me']);

  const { country, correspondenceLanguage, globalId, localId, name, hangul } =
    childDetails;

  const note = getNote(childDetails);

  const countryIso = countryToIso(country, localId);
  const countryKey = `geo:countries.${countryIso}.nameS`;
  const translatedCountry = t(countryKey);
  const languageKey = key('bio:language', correspondenceLanguage);
  const translatedLanguage = t(languageKey);

  const { isSubmitting, bioErrors, handleSponsorshipSubmit } =
    useChildSponsorship(globalId);

  return (
    <div className="HelpMeChooseResult">
      <div className="ChildBio ChildBio__flex flex flex-col">
        <div className="ChildBio__content flex flex-col sm:flex-row gap-comfy-sm sm:gap-comfy-xl">
          <div className="ChildBio__media flex-1 align-start">
            <div className="sm:hidden">
              <ChildBioDetailsHeading
                seoLevel={2}
                hangul={hangul}
                name={name}
              />
            </div>
            <ChildBioImage
              details={childDetails}
              availability={childAvailability}
            />
          </div>
          <div className="ChildBio__summary flex flex-col flex-1 gap-comfy-sm sm:gap-0 sm:pt-0">
            <div className="hidden sm:block">
              <ChildBioDetailsHeading
                seoLevel={2}
                hangul={hangul}
                name={name}
              />
            </div>
            <ChildBioSummary
              className="mb-0 sm:pb-comfy-sm text-sm order-1"
              details={childDetails}
              translatedCountry={translatedCountry}
              translatedLanguage={translatedLanguage}
            />
            <SubmitButton
              className="order-2 sm:order-last"
              bioErrors={bioErrors}
              details={childDetails}
              isSubmitting={isSubmitting}
              handleSponsorshipSubmit={handleSponsorshipSubmit}
            />
            <div className="order-3 sm:pb-comfy-xl">
              <ChildBioInfo
                details={childDetails}
                countryKey={countryKey}
                note={note}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
